import React, { useState } from 'react';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { TextField } from '../../../components/TextField';
import { BusinessOnboardingService } from '../../../api/generated';
import { ROUTES } from '../../../config/routes';
import { ReactComponent as CloseIcon } from '../../../assets/icons/x.svg';

interface CreateBusinessDraftModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateBusinessDraftModal: React.FC<CreateBusinessDraftModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setName('');
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Business name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await BusinessOnboardingService.postApiBusinessOnboardingCreateDraftBusinessApplication({
        name: name.trim(),
      });

      if (response.draftBusinessApplicationId) {
        handleClose();
        navigate(ROUTES.PROMOTIONS_DRAFT_EDIT(response.draftBusinessApplicationId), {
          state: { successMessage: 'Draft created successfully!' },
        });
      }
    } catch (err: any) {
      setError(
        err?.body?.message ||
        err?.message ||
        'Failed to create draft. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal-content md:max-w-[500px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Register New Business"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Register New Business</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <TextField
          type="text"
          id="name"
          name="name"
          label="Business Name"
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          required
          disabled={loading}
        />

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={loading}>
            Next
          </Button>
        </div>
      </form>
    </Modal>
  );
};
